import type { PayloadAction } from '@reduxjs/toolkit';
import { createSelector, createSlice } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';

import type { Dispatch, RootState } from 'src/app/state/store';
import type { Attachment, ChatHistoryMessage } from 'src/app/types';
import { IS_MOBILE } from 'src/consts';
import { LOCALIZED_CONTACT_INFO } from 'src/consts/geo';

type ChatState = {
  status: 'closed' | 'open';
  auth: {
    isFetching: boolean;
    error: string | null;
    ticket: string | null;
  };
  history: {
    isFetching: boolean;
    error: string | null;
    list: ChatHistoryMessage[];
    unreadMessageCount: number;
  };
  prepopulatedMessage?: string;
  source?: string;
};

export const initialState: ChatState = {
  status: 'closed',
  auth: {
    isFetching: false,
    error: null,
    ticket: null,
  },
  history: {
    isFetching: false,
    error: null,
    list: [],
    unreadMessageCount: 0,
  },
  prepopulatedMessage: undefined,
  source: undefined,
};

export const CHAT_HISTORY_LENGTH = 50;

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    openChat(state) {
      state.status = 'open';
      state.auth.isFetching = false;
      state.history.isFetching = false;
    },
    closeChat(state) {
      state.status = 'closed';
      state.auth.isFetching = false;
      state.history.isFetching = false;
    },
    getChatAuthTicket(state) {
      state.auth.isFetching = true;
    },
    setChatAuthTicket(state, action: PayloadAction<string>) {
      state.auth.isFetching = false;
      state.auth.ticket = action.payload;
    },
    getChatHistory(state) {
      state.history.isFetching = true;
    },
    setChatHistory(state, action: PayloadAction<ChatHistoryMessage[]>) {
      state.history.isFetching = false;
      state.history.list = action.payload;
    },
    setChatHistoryFailure(state, action: PayloadAction<string>) {
      state.history.isFetching = false;
      state.history.error = action.payload;
    },
    setChatHistoryUnreadCount(state, action: PayloadAction<number>) {
      state.history.unreadMessageCount = action.payload;
    },
    setChatPrepopulatedMessage(state, action: PayloadAction<string>) {
      state.prepopulatedMessage = action.payload;
    },
    clearChatPrepopulatedMessage(state) {
      state.prepopulatedMessage = undefined;
    },
    setChatSource(state, action: PayloadAction<string>) {
      state.source = action.payload;
    },
    clearChatSource(state) {
      state.source = undefined;
    },
  },
});

/**
 * Thunks
 *
 * @description For when you need to do async stuff or when you need to
 * access state from other slices/reducers.
 */
const markChatAsRead = () => async (dispatch: Dispatch, getState: () => RootState) => {
  const state = getState();
  const { client } = state;
  try {
    await client.markChatAsRead();
    dispatch(chatSlice.actions.setChatHistoryUnreadCount(0));
  } catch (error) {
    Sentry.captureException(error);
  }
};

const getAndSetChatHistory = () => async (dispatch: Dispatch, getState: () => RootState) => {
  const state = getState();
  dispatch(chatSlice.actions.getChatHistory());

  try {
    const type = 'chat';
    const { data } = await state.client.getChatHistory(CHAT_HISTORY_LENGTH, type);

    dispatch(chatSlice.actions.setChatHistory(data));
  } catch (error: any) {
    const actualError = error?.message ?? error ?? 'Something went wrong.';
    dispatch(chatSlice.actions.setChatHistoryFailure(actualError));
    Sentry.captureException(error);
  }
};

const getAndSetAuthTicket = () => async (dispatch: Dispatch, getState: () => RootState) => {
  const state = getState();
  dispatch(chatSlice.actions.getChatAuthTicket());

  try {
    const response = await state.client.getChatAuthTicket();
    const authTicket = response.data.ticket;
    dispatch(chatSlice.actions.setChatAuthTicket(authTicket));
  } catch (error) {
    Sentry.captureException(error);
  }
};

export const openChat =
  (props?: { prepopulatedMessage?: string; source?: string }) => (dispatch: Dispatch, getState: () => RootState) => {
    if (typeof window === 'undefined') return;

    // This right now is only used for analytics so we know which page/source the open chat request
    // came from. This is used mainly for the reschedule order work where sometimes we route the user
    // to chat over self service.
    if (props?.source) {
      dispatch(chatSlice.actions.setChatSource(props.source));
    }

    const isLikelyToHaveMessagingApp = IS_MOBILE || window.navigator.userAgent.match(/(iPad)/i);

    if (isLikelyToHaveMessagingApp) {
      const preferredLocation: keyof typeof LOCALIZED_CONTACT_INFO.CAPSULE_TEXT_NUMBER =
        getState().location?.preferredLocation ?? 'DEFAULT';
      const numberToContact = LOCALIZED_CONTACT_INFO.CAPSULE_TEXT_NUMBER[preferredLocation];

      const link = document.createElement('a');
      const predefinedMessage = props?.prepopulatedMessage ? `;?&body=${encodeURI(props.prepopulatedMessage)}` : '';
      console.log({ numberToContact, predefinedMessage });

      link.href = `sms:${numberToContact}${predefinedMessage}`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }

    /***** Not Mobile *****/
    if (props?.prepopulatedMessage) {
      dispatch(chatSlice.actions.setChatPrepopulatedMessage(props.prepopulatedMessage));
    }
    dispatch(chatSlice.actions.openChat());
    dispatch(getAndSetAuthTicket());
    dispatch(getAndSetChatHistory());
    dispatch(markChatAsRead());
  };

export const clearChatPrepopulatedMessage = () => (dispatch: Dispatch) => {
  dispatch(chatSlice.actions.clearChatPrepopulatedMessage());
};

export const clearChatSource = () => (dispatch: Dispatch) => {
  dispatch(chatSlice.actions.clearChatSource());
};

export const closeChat = () => (dispatch: Dispatch) => {
  dispatch(chatSlice.actions.closeChat());
  dispatch(chatSlice.actions.setChatHistory([]));
  dispatch(clearChatPrepopulatedMessage());
  dispatch(clearChatSource());
};

export const checkForUnreadMessages = () => async (dispatch: Dispatch, getState: () => RootState) => {
  const state = getState();
  const { client } = state;

  try {
    const { data } = await client.getChatUnreadMessagesCount();
    const unreadMessageCount = data.count as number;
    dispatch(chatSlice.actions.setChatHistoryUnreadCount(unreadMessageCount));
  } catch (error) {
    Sentry.captureException(error);
  }
};

export const uploadChatImage = (image: Blob) => async (_dispatch: Dispatch, getState: () => RootState) => {
  const state = getState();

  const imageType: 'image/jpeg' | 'image/png' | string = image.type;

  const isAllowedImage = imageType === 'image/jpeg' || imageType === 'image/png';

  if (!isAllowedImage) {
    // UI should prevent this from happening, but just in case
    throw new Error(`Invalid image type: ${imageType}`);
  }

  try {
    const bffResponse = await state.client.uploadChatImage(imageType.split('image/').pop());

    const filepath: string | undefined = bffResponse?.data?.filepath;
    const uploadUrl: string | undefined = bffResponse?.data?.uploadUrl;

    if (!uploadUrl) {
      throw new Error('Upload URL not found in response');
    }

    const { status } = await fetch(uploadUrl, { method: 'PUT', body: image });

    if (status === 200) {
      const attachent: Attachment = { filepath: filepath!, mime_type: imageType };
      return attachent;
    } else {
      throw new Error('s3 did not respond with 200');
    }
  } catch (error) {
    Sentry.captureException(error);
    return null;
  }
};

export const downloadChatImage = (filepath: string) => async (_dispatch: Dispatch, getState: () => RootState) => {
  const state = getState();

  try {
    const bffResponse = await state.client.downloadChatImage(filepath);

    if (bffResponse.data.status === 'SUCCESS') {
      return bffResponse.data.downloadUrl as string;
    } else {
      throw new Error(JSON.stringify(bffResponse.data));
    }
  } catch (error) {
    Sentry.captureException(error);
    return null;
  }
};

/** Selectors */
const getChat = (state: RootState) => state?.chat;
export const getCapsuleChatAuthTicket = createSelector(getChat, state => state?.auth.ticket as string | undefined);
export const getCapsuleChatHistoryMessages = createSelector(
  getChat,
  state => (state?.history.list ?? []) as ChatHistoryMessage[]
);
export const getCapsuleChatHistoryLoading = createSelector(getChat, state => state?.history.isFetching as boolean);
export const isChatOpenSelector = createSelector(getChat, state => (state?.status === 'open') as boolean);
export const unreadChatMessageCountSelector = createSelector(
  getChat,
  state => (state?.history.unreadMessageCount ?? 0) as number
);

export const getChatPrepopulatedMessage = createSelector(getChat, state => state?.prepopulatedMessage as string);
export const getChatSource = createSelector(getChat, state => state?.source as string);

export default chatSlice.reducer;
